import * as React from 'react';
import * as TypeStyle from 'typestyle';
import { TextColors } from '../../styles/Colors';
import { TabStyles } from '../../styles/TabStyles';
import { makeTestId } from '../../utils/Declarative';
import { generateId } from '../../utils/DomUtil';
import { EventUtilsOptOut, PropsAndElementAttrs } from '../../utils/ReactHelpers';
import { RadioButtonWithLabelWidget } from './SelectorShims';

export enum RadioLayoutType {
  Veritical = 0,
  Horizontal = 1,
}

export interface RadioButtonWithLabelGroupProps<T = string> extends Pick<React.AriaAttributes, 'aria-labelledby'> {
  /** The description of the radio button group that will be read by screen readers */
  ariaLabel?: string;
  /** The list of Radio Button Options to be created */
  buttonList: RadioButtonWithLabelOption<T>[];
  /** Which radio button should be checked */
  checkedIndex?: number;
  /** Callback to being changed */
  handleChange: (value: T, checkedIndex: number) => void;
  /** whether the options should be justified and evenly spaced horizontally */
  justifyHorizontalItems?: boolean;
  /** Label to display above the radio group, if any. If omitted, you must instead supply `ariaLabel` or `aria-labelledby` */
  label?: React.ReactNode;
  /** whether the options should be displayed in a horizontal or vertical layout */
  layout?: RadioLayoutType;
  /** The group name attribute.  If not given, a unique name will be generated */
  name?: string;
  /** If the default left margin should be removed */
  removeLeftMargin?: boolean;
  role?: never;
  /** Will be prefixed with `'RadioButtonWithLabelGroupWidget-'`  */
  testId?: string;
}

export interface RadioButtonWithLabelOption<T = string> {
  /**
   * Additional content shown below the label. Any interactive content (e.g. text fields, nested radio groups) should be put here, not in `label`.
   * Best practice is generally to disable or hide any interactive `detail` content belonging to an unselected radio button.
   */
  detail?: React.ReactNode
  /** Disabled state of the radio option */
  disabled?: boolean;
  /**
   * Label of the radio option. This should only explain the radio button's value. It must not contain interactive content like links, fields, etc.
   * Instead use `detail` for any interactive content, and/or particularly long text blocks which go beyond simply explaining the value.
   */
  label: React.ReactNode;
  /** Test id to be attached to the radio option */
  testId: string;
  /** Value returned in the `handleChange` event triggered by selecting this radio option */
  value: T;
}

const StyleName = TypeStyle.style({
  color: TextColors.Primary,
  fontSize: TabStyles.Typography.Sizing.Default,
  $nest: {
    '&.horizontal': { display: 'flex', flexWrap: 'wrap' },
    '&.horizontal.justify': { justifyContent: 'space-between' },
    '&.horizontal > :not(:last-child)': { marginRight: TabStyles.Spacing.ExtraLargeSpace },
  }
});

/** Displays a group of radio options */
export class RadioButtonWithLabelGroupWidget<T = string> extends React.Component<PropsAndElementAttrs<RadioButtonWithLabelGroupProps<T>>> {
  private groupId: string = generateId();

  public constructor(props: RadioButtonWithLabelGroupWidget<T>['props']) {
    super(props);
    if (!props.label && !(props.ariaLabel || props['aria-label']) && !props['aria-labelledby']) {
      console.trace('`label`, `aria-label`, or `aria-labelledby` must be provided for every radio group');
    }
  }

  public render(): JSX.Element {
    // eslint-disable-next-line max-len
    const { ariaLabel, buttonList, checkedIndex, handleChange, justifyHorizontalItems, label, layout, name, removeLeftMargin, testId, ...htmlAttrs } = this.props;

    const hasDetail = buttonList.some(button => button.detail);
    const buttons = buttonList.map(({ detail, ...buttonOption }, index) =>
      <RadioButtonWithLabelWidget
        {...buttonOption}
        checked={index === checkedIndex}
        groupName={name || this.groupId}
        handleChange={handleChange}
        index={index}
        key={index}
        removeLeftMargin={removeLeftMargin}
      />
    ).map((buttonElem, index) => {
      if (!hasDetail) { return buttonElem; }
      const { detail, disabled } = buttonList[index];
      const detailId = `${this.groupId}-${index}`;

      return <div key={index}>
        {React.cloneElement<RadioButtonWithLabelWidget['props']>(buttonElem, { 'aria-describedby': buttonElem.props['aria-describedby'] ?? detailId })}
        {detail && <div style={{ color: disabled ? TextColors.Disabled : undefined, marginLeft: removeLeftMargin ? 20 : 26 }} id={detailId}>
          {detail}
        </div>}
      </div>
    });
    const labelId = label ? this.groupId + '-label' : undefined;

    return (
      <div
        data-tb-test-id={makeTestId('RadioButtonWithLabelGroupWidget', testId)}
        {...htmlAttrs}
        aria-label={ariaLabel ?? htmlAttrs['aria-label']}
        aria-labelledby={htmlAttrs['aria-labelledby'] ?? labelId}
        className={TypeStyle.classes(StyleName, htmlAttrs.className, {
          horizontal: layout === RadioLayoutType.Horizontal,
          justify: justifyHorizontalItems,
        })}
        role='radiogroup'
      >
        {label && <RadioGroupLabel children={label} id={labelId} />}
        {buttons}
      </div>
    );
  }
}

const LabelStyleName = TypeStyle.style({ height: `21px`, lineHeight: `21px` });
const RadioGroupLabel: React.FunctionComponent<React.LabelHTMLAttributes<HTMLLabelElement>> = props => {
  const { onClick, ...htmlAttrs } = props;

  const handleClick = React.useCallback<React.MouseEventHandler<HTMLLabelElement>>(e => {
    if (!e.currentTarget.htmlFor) { return; }

    const focusTarget = document.getElementById(e.currentTarget.htmlFor);
    if (focusTarget && !(focusTarget.tabIndex === null || focusTarget.tabIndex === undefined)) {
      focusTarget.focus();
    }
    onClick?.(e);
  }, [onClick]);

  return (
    // eslint-disable-next-line jsx-a11y/label-has-associated-control, jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
    <label
      {...htmlAttrs}
      {...EventUtilsOptOut}
      className={LabelStyleName}
      onClick={handleClick}
    />
  );
};
RadioGroupLabel.displayName = 'RadioGroupLabel';
